import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "ui/button";
import { useState, useEffect } from "react";
import { StateCamera, createCamera } from "lib/actions";
import { CameraSearchDataType, WsResDataType } from "lib/types";
import CameraCreateItem from "ui/streaming/camera-create-item";
import DataControllerManager from "lib/dataControler";
import { showToast } from "ui/components/common";
import { useCustomersService } from "pages/dashboard/customers/service";
import { useCommonService } from "pages/service";
import type { SnapshotDataTypes } from "pages/service";
import { CameraStreamType } from "webrtc-player/common/base.js";
export default function Form({
  orgId,
  siteId,
  nvrId,
}: {
  orgId: string;
  siteId: string;
  nvrId: string;
}) {
  const [cameras, setCameras] = useState<CameraSearchDataType[]>([]);
  const [checkedIndexes, setCheckedIndexes] = useState<number[]>([]);
  const [snapshots, setSnapshots] = useState<SnapshotDataTypes[]>([]);
  const { sendUpdateCameraSettingRequest, sendSearchDeviceRequest } =
    useCustomersService();
  const { takeSnapshot } = useCommonService();

  useEffect(() => {
    doFetchSnapshot(cameras);
  }, [cameras]);

  const doFetchSnapshot = (cameras) => {
    DataControllerManager.orgId = orgId;
    const dataNvrsUnunique = cameras?.map((item) => item.nvrId);
    const dataNvrs = dataNvrsUnunique?.filter(function (value, index, array) {
      return array.indexOf(value) === index;
    });
    dataNvrs?.forEach((nvrId) => {
      const camerasForNvr = cameras
        ?.filter((item) => item.nvrId === nvrId)
        ?.map(({ id, macAddress, remoteIndex, capabilities }) => {
          let streamId = 0;
          if (capabilities?.streamNum > 1) streamId = 1;
          return { streamId, remoteIndex, id, macAddress };
        });
      const chunkCameras: CameraStreamType[][] = [];
      const chunkSize = 16;
      for (let i = 0; i < camerasForNvr.length; i += chunkSize) {
        chunkCameras.push(camerasForNvr.slice(i, i + chunkSize));
      }
      chunkCameras.forEach(async (chunk) => {
        await takeSnapshot(nvrId, chunk, orgId, (snapshots) => {
          snapshots?.forEach((snapshot) => {
            setSnapshots((snapshots) => {
              const newSnapshots = snapshots.filter(
                (old) =>
                  old.macAddress !== snapshot.macAddress ||
                  old.remoteIndex !== snapshot.remoteIndex,
              );
              return [...newSnapshots, snapshot];
            });
          });
        });
      });
    });
  };

  useEffect(() => {
    const initSearchDevice = async () => {
      try {
        const res = await sendSearchDeviceRequest({ nvrId: nvrId }, orgId);
        const data = (res as WsResDataType)?.deviceSearchReply?.cameraList;
        setCameras(data || []);
        setCheckedIndexes(data?.map((_, index) => index) || []);
      } catch (error) {
        showToast(1, "There was a problem searching cameras. Error:" + error);
      }
    };
    initSearchDevice();
  }, [nvrId]);

  const handleUpdateCameraName = async (
    cameras: { id: number; name: string }[],
  ) => {
    try {
      const res = await sendUpdateCameraSettingRequest(cameras, orgId);
      const data = (res as WsResDataType)?.deviceUpdateCamerasSettingsReply
        ?.data;

      if (data?.every((e) => !e?.nameRes?.error)) {
        showToast(0, "The Camera name has been updated successfully!");
      } else {
        showToast(1, "Failed to update the Camera. Please try again later.");
      }
      console.log("handleUpdateCameraName", res);
    } catch (error) {
      console.error(error);
    }
  };

  const navigate = useNavigate();
  const [responseData, setResponseData] = useState<StateCamera>();
  const [btnSubmitDisabled, setBtnSubmitDisabled] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const selectedCameras = cameras.filter((_, index) =>
      checkedIndexes.includes(index),
    );
    try {
      setBtnSubmitDisabled(true);
      const response = await createCamera(
        orgId,
        siteId,
        nvrId,
        JSON.stringify(selectedCameras),
      );

      console.log("response", response);
      const updateData: { id: number; name: string }[] = [];
      response?.data?.forEach((camera) => {
        const _cameras = cameras;
        const oriCamera = _cameras.find(
          (item) =>
            item.macAddress === camera.macAddress &&
            item.remoteIndex === camera.remoteIndex,
        );
        updateData.push({ name: oriCamera.name, id: camera.cameraId });
      });
      await handleUpdateCameraName(updateData);
      setBtnSubmitDisabled(false);
      setResponseData(response);
      navigate(`/dashboard/customers`);
    } catch (error) {
      console.error(error);
    }
  }

  const onCameraNameChanged = (index: number, newName: string) => {
    cameras[index].name = newName;
  };

  const onCheckedChanged = (checked: boolean, index: number) => {
    if (checked) {
      setCheckedIndexes((checkedIndexes) => [...checkedIndexes, index]);
    } else {
      setCheckedIndexes((checkedIndexes) =>
        checkedIndexes.filter((i) => i !== index),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-4 md:p-6 max-w-lg mx-auto">
        <h2 className="text-lg font-medium mb-5">
          Last step, add cameras to this site.
        </h2>
        <p className="text-sm mb-3">
          Here are the cameras connected to the NVR. Hover on the camera name to
          edit.
        </p>
      </div>
      <div className="grid grid-cols-4 gap-4 w-full max-w-5xl mx-auto">
        {cameras &&
          cameras.map((objCamera, index) => {
            const objSnapshot = snapshots.find((item) => {
              return (
                item!.macAddress === objCamera.macAddress &&
                item!.remoteIndex === objCamera.remoteIndex
              );
            });

            return (
              <CameraCreateItem
                onCameraNameChanged={onCameraNameChanged}
                snapshot={objSnapshot}
                objCamera={objCamera}
                index={index}
                checked={checkedIndexes.includes(index)}
                onChecked={(checked) => {
                  onCheckedChanged(checked, index);
                }}
                key={index}
              />
            );
          })}
      </div>
      <div className="mt-6 flex justify-end gap-4 max-w-lg mx-auto">
        <Link
          to="/dashboard/customers"
          className="flex h-14 px-8 items-center rounded-2xl bg-gray-100  text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
        >
          Cancel
        </Link>
        <Button
          type="submit"
          aria-disabled={btnSubmitDisabled || checkedIndexes.length === 0}
          disabled={btnSubmitDisabled || checkedIndexes.length === 0}
        >
          Continue
        </Button>
      </div>
      {responseData?.message && (
        <div
          className="p-4 mb-4 mt-12 text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 flex items-center space-x-1"
          role="alert"
          aria-live="polite"
          aria-atomic="true"
        >
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-500">{responseData.message}</p>
        </div>
      )}
    </form>
  );
}
